import React, { useState, useRef, useEffect } from 'react';
import { Layout, Button, Space, Toast, Card, InputNumber, TextArea, Modal } from '@douyinfe/semi-ui';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { ImageSelector } from '../../components/ImageSelector';
import LinkSelector from '../../components/LinkSelector';
import DroppableArea from './DroppableArea';
import styles from './index.module.scss';
import { copyToClipboard } from '../../utils/clipboard'; // 引入 copyToClipboard

const { Header, Sider, Content } = Layout;

const HotspotEditor = () => {
    const [image, setImage] = useState('');
    const [hotspots, setHotspots] = useState([]);
    const [selectedHotspotId, setSelectedHotspotId] = useState(null); // 修改为selectedHotspotId
    const imageRef = useRef(null);
    const [hotspotConfig, setHotspotConfig] = useState('');
    const [editableConfig, setEditableConfig] = useState('');
    const [isManualEdit, setIsManualEdit] = useState(false);

    useEffect(() => {
        const configStr = JSON.stringify({
            type: "single-image",
            image: image,
            content: JSON.stringify(hotspots.map(({ id, link, width, height, ...rest }) => ({ ...rest, l: link, w: width, h: height })))
        }, null, 2);
        setHotspotConfig(configStr);
        // 只有在用户没有手动编辑时才自动更新
        if (!isManualEdit) {
            setEditableConfig(configStr);
        }
    }, [image, hotspots, isManualEdit]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Delete' && selectedHotspotId) { // 使用selectedHotspotId
                handleDeleteHotspot(selectedHotspotId);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [selectedHotspotId, hotspots]); // 依赖项改为selectedHotspotId

    const handleConfigChange = (newConfigString) => {
        try {
            const newConfig = JSON.parse(newConfigString);
            setImage(newConfig.image || '');
            setHotspots(newConfig.content ? JSON.parse(newConfig.content).map((h, index) => ({ ...h, id: h.id || `temp-${index}`, link: h.l, width: h.w, height: h.h })) : []);
        } catch (error) {
            Toast.error('配置格式不正确');
            console.error('Error parsing hotspot config:', error);
        }
    };

    const handleApplyConfig = () => {
        try {
            const newConfig = JSON.parse(editableConfig);
            setImage(newConfig.image || '');
            const newHotspots = newConfig.content ? JSON.parse(newConfig.content).map((h, index) => ({
                ...h,
                id: h.id || `temp-${index}`,
                link: h.l,
                width: h.w,
                height: h.h
            })) : [];
            setHotspots(newHotspots);
            setSelectedHotspotId(null);
            setIsManualEdit(false); // 应用后重置手动编辑标记
            Toast.success('配置应用成功');
        } catch (error) {
            Toast.error('配置格式不正确，请检查JSON语法');
            console.error('Error parsing hotspot config:', error);
        }
    };

    const handleTextAreaChange = (value) => {
        setEditableConfig(value);
        setIsManualEdit(true);
    };

    // 添加热区
    const handleAddHotspot = (newHotspot) => {
        if (!image) {
            Toast.error('请先选择图片');
            return;
        }

        setHotspots([...hotspots, newHotspot]);
        setSelectedHotspotId(newHotspot.id); // 设置选中热区的ID
    };

    // 删除热区
    const handleDeleteHotspot = (id) => {
        Modal.confirm({
            title: '确认删除',
            content: '确定要删除此热区吗？',
            onOk: () => {
                setHotspots(hotspots.filter(h => h.id !== id));
                if (selectedHotspotId === id) { // 使用selectedHotspotId
                    setSelectedHotspotId(null);
                }
            },
        });
    };

    // 更新热区位置和大小
    const handleHotspotChange = (id, changes) => {
        console.log('handleHotspotChange called with id:', id, 'changes:', changes);
        setHotspots(hotspots.map(h => {
            if (h.id === id) {
                return { ...h, ...changes };
            }
            return h;
        }));

        // 如果当前热区被选中，也更新选中的热区数据
        if (selectedHotspotId === id) { // 使用selectedHotspotId
            // 不需要更新selectedHotspot，因为我们现在只存储ID
        }
    };

    // 更新热区链接
    const handleLinkChange = (id, link) => {
        handleHotspotChange(id, { link });
    };

    const handleHotspotSelect = (hotspot) => {
        setSelectedHotspotId(hotspot ? hotspot.id : null);
    };

    const selectedHotspot = hotspots.find(h => h.id === selectedHotspotId); // 根据ID查找选中的热区

    return (
        <DndProvider backend={HTML5Backend}>
            <Layout className={styles.editorContainer}>
                <Header className={styles.header}>
                    <Space>
                        {/* 保存按钮已移除，改为实时显示和编辑JSON配置 */}
                    </Space>
                </Header>
                <Layout>
                    <Content className={styles.content}>
                        <div className={styles.imageContainer} ref={imageRef}>
                            {image ? (
                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                    <DroppableArea
                                        key={selectedHotspotId || 'no-hotspot'} // 使用selectedHotspotId
                                        image={image}
                                        hotspots={hotspots}
                                        selectedHotspot={selectedHotspot} // 传递完整的selectedHotspot对象
                                        onHotspotSelect={handleHotspotSelect}
                                        onHotspotChange={handleHotspotChange}
                                        onAddHotspot={handleAddHotspot}
                                    />
                                </div>
                            ) : (
                                <div className={styles.imagePlaceholder}>
                                    <ImageSelector value={image} onChange={setImage} />
                                </div>
                            )}
                        </div>
                        <Card
                            title="热区配置JSON"
                            headerStyle={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                            style={{ marginTop: '20px', width: '100%' }}
                        >
                            <Space style={{ marginBottom: '16px' }}>
                                <Button
                                    icon="copy"
                                    onClick={() => {
                                        copyToClipboard(editableConfig).then(() => {
                                            Toast.success('配置已复制');
                                        }).catch(err => {
                                            console.error('Failed to copy text: ', err);
                                            Toast.error('复制失败');
                                        });
                                    }}
                                >
                                    复制配置
                                </Button>
                                <Button
                                    type="primary"
                                    onClick={handleApplyConfig}
                                >
                                    应用配置
                                </Button>

                            </Space>
                            <textarea
                                value={editableConfig}
                                onChange={(e) => handleTextAreaChange(e.target.value)}
                                placeholder="请输入JSON配置"
                                style={{
                                    width: '100%',
                                    height: '400px',
                                    fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                                    fontSize: '14px',
                                    lineHeight: '1.4',
                                    padding: '12px',
                                    border: '1px solid #d9d9d9',
                                    borderRadius: '6px',
                                    resize: 'vertical',
                                    backgroundColor: '#fafafa'
                                }}
                            />
                        </Card>
                    </Content>
                    <Sider className={styles.sider}> {/* 右侧Sider */}
                        {selectedHotspot && ( // 只有当有选中热区时才显示设置卡片
                            <Card title="热区设置">
                                <div style={{ padding: '20px' }}>
                                    <div style={{ marginBottom: '16px' }}>
                                        <label style={{ display: 'inline-block', width: '80px', textAlign: 'right', marginRight: '8px' }}>链接设置</label>
                                        <LinkSelector
                                            value={selectedHotspot.link}
                                            onChange={value => handleLinkChange(selectedHotspot.id, value)}
                                        />
                                    </div>
                                    <Button
                                        type="danger"
                                        onClick={() => handleDeleteHotspot(selectedHotspot.id)}
                                        style={{ marginTop: 16 }}
                                    >
                                        删除热区
                                    </Button>
                                </div>
                            </Card>
                        )}
                    </Sider>
                </Layout>
            </Layout>
        </DndProvider >
    );
};

export default HotspotEditor;